import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Badge, Card, Empty, Kpi, Loaded, Notice, Page, ReasonDialog, Table } from '../components/ui'
import type { Column } from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, dateTime, minor, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type CoachRow = {
  id: string
  user_id: string
  display_name: string
  speciality: string
  status: string
  credential: string
  years_experience: number
  rating: number
  review_count: number
  active_clients: number
  accepting_clients: boolean
  verified_at: string | null
  listing_paid_until: string | null
  created_at: string | null
}

type CoachPage = {
  items: CoachRow[]
  total: number
  page: number
  page_size: number
  by_status: Record<string, number>
}

type Overview = {
  coaches_by_status: Record<string, number>
  awaiting_verification: number
  active_subscriptions: number
  monthly_gross_minor: number
  unmatched_events: number
  gateways: { gateway: string; ready: boolean }[]
}

type Tone = 'alert' | 'cyan' | 'green' | 'lime' | 'orange'

const STATUS_TONE: Record<string, Tone> = {
  live: 'green',
  draft: 'orange',
  delisted: 'lime',
  suspended: 'alert',
}

const TABS = ['draft', 'live', 'delisted', 'suspended'] as const

export function Coaches() {
  const { permissions } = useAuth()
  const canModerate = permissions.has(P.marketplaceModerate)
  const run = useAction()

  const [status, setStatus] = useState<string>('draft')
  const [query, setQuery] = useState('')
  const [verifying, setVerifying] = useState<CoachRow | null>(null)

  const overview = useResource((signal) =>
    get<Overview>('/v1/admin/marketplace/overview', undefined, signal),
  )
  const coaches = useResource(
    (signal) => get<CoachPage>('/v1/admin/coaches', { status, q: query }, signal),
    [status, query],
  )

  const reload = () => {
    coaches.reload()
    overview.reload()
  }

  const columns: Column<CoachRow>[] = [
    {
      key: 'name',
      header: 'Coach',
      strong: true,
      render: (row) => <Link to={`/coaches/${row.id}`}>{row.display_name || '—'}</Link>,
    },
    {
      key: 'speciality',
      header: 'Practises',
      render: (row) => titleCase(row.speciality.replace('_', ' ')),
    },
    {
      key: 'credential',
      header: 'Claimed credential',
      // The whole point of the queue: what this person says they are, which
      // somebody has to go and check.
      render: (row) => <span className="mono">{row.credential || '— none given —'}</span>,
    },
    {
      key: 'experience',
      header: 'Years',
      render: (row) => count(row.years_experience),
    },
    {
      key: 'clients',
      header: 'Clients',
      render: (row) => count(row.active_clients),
    },
    {
      key: 'waiting',
      header: 'Registered',
      render: (row) => dateTime(row.created_at),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge tone={STATUS_TONE[row.status]}>{titleCase(row.status)}</Badge>
      ),
    },
    {
      key: 'act',
      header: '',
      render: (row) =>
        canModerate && row.status === 'draft' ? (
          <button className="btn btn--sm btn--primary" onClick={() => setVerifying(row)}>
            Verify…
          </button>
        ) : null,
    },
  ]

  return (
    <Page
      title="Coaches"
      subtitle="Who appears when someone in the app looks for a dietitian, a trainer or a mental-health professional."
      actions={
        <input
          className="input"
          placeholder="Search by name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search coaches"
        />
      }
    >
      <Notice tone={overview.data && overview.data.awaiting_verification > 0 ? 'warn' : undefined}>
        <strong>Nothing lists itself.</strong> A registration starts in <span className="mono">draft</span>{' '}
        and stays invisible until somebody here checks the credential and says what they checked.
        That note is the only record the verification happened, so write what you actually looked
        at — a registration number, a certificate — not "verified".
      </Notice>

      <Loaded resource={overview}>
        {(data) => (
          <div className="grid grid--kpi">
            <Kpi
              label="Awaiting verification"
              value={count(data.awaiting_verification)}
              tone={data.awaiting_verification > 0 ? 'orange' : undefined}
              foot="people waiting on somebody here"
            />
            <Kpi label="Listed" value={count(data.coaches_by_status.live ?? 0)} tone="green" />
            <Kpi
              label="Active subscriptions"
              value={count(data.active_subscriptions)}
              tone="cyan"
            />
            <Kpi
              label="Monthly gross"
              value={minor(data.monthly_gross_minor)}
              foot="what active clients pay their coaches"
            />
            <Kpi
              label="Unmatched webhooks"
              value={count(data.unmatched_events)}
              tone={data.unmatched_events > 0 ? 'alert' : undefined}
              foot="payments that reached no subscription"
            />
          </div>
        )}
      </Loaded>

      <Card
        title="Listings"
        hint="Oldest first — a verification queue is a queue"
        action={
          <div className="row">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={status === tab ? 'btn btn--sm btn--primary' : 'btn btn--sm btn--ghost'}
                onClick={() => setStatus(tab)}
              >
                {titleCase(tab)}
                {coaches.data?.by_status?.[tab] ? ` · ${coaches.data.by_status[tab]}` : ''}
              </button>
            ))}
            <button
              className={status === '' ? 'btn btn--sm btn--primary' : 'btn btn--sm btn--ghost'}
              onClick={() => setStatus('')}
            >
              All
            </button>
          </div>
        }
      >
        <Loaded resource={coaches} emptyWhen={(d) => d.items.length === 0} empty={
          status === 'draft' ? (
            <Empty>Nobody is waiting on a credential check.</Empty>
          ) : (
            <Empty>No listings in this state.</Empty>
          )
        }>
          {(data) => <Table columns={columns} rows={data.items} rowKey={(row) => row.id} />}
        </Loaded>
      </Card>

      {verifying && (
        <ReasonDialog
          title={`Verify ${verifying.display_name}`}
          description={
            <>
              This puts the listing in front of people asking for health advice. Say what you
              checked — the claimed credential is{' '}
              <span className="mono">{verifying.credential || 'not stated'}</span>.
            </>
          }
          confirmLabel="Verify and list"
          minLength={10}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/coaches/${verifying.id}/verify`, { reason }),
              `${verifying.display_name} is listed`,
              reload,
            )
          }
          onClose={() => setVerifying(null)}
        />
      )}
    </Page>
  )
}
